/**
 * Token Utility Functions
 *
 * Provides utilities for token management, including automatic refresh
 * before API calls when tokens are expired or close to expiring.
 */

import * as cognitoAuth from "services/cognitoAuth";

/**
 * Check if a JWT token is expired or will expire soon
 * @param {string} token - JWT token string
 * @param {number} bufferSeconds - Buffer time in seconds before expiration to consider token as expired (default: 60 seconds)
 * @returns {boolean} - True if token is expired or will expire soon
 */
export const isTokenExpired = (token, bufferSeconds = 60) => {
  if (!token) return true;

  try {
    const decodedToken = decodeJWT(token);
    if (!decodedToken || !decodedToken.exp) {
      return true;
    }

    // Convert expiration time to milliseconds
    const expirationTime = decodedToken.exp * 1000;
    const currentTime = Date.now();
    const bufferTime = bufferSeconds * 1000;

    // Token is expired if current time + buffer >= expiration time
    return currentTime + bufferTime >= expirationTime;
  } catch (error) {
    console.error("Error checking token expiration:", error);
    return true; // If we can't decode, consider it expired
  }
};

/**
 * Decode JWT token without verification (client-side only)
 * @param {string} token - JWT token string
 * @returns {Object|null} - Decoded token payload or null if invalid
 */
export const decodeJWT = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error("Error decoding JWT:", e);
    return null;
  }
};

/**
 * Get current user context from ID token (custom claims).
 * Used for menu, navbar, and "current" role/entity after profile switch.
 * @param {string} idToken - Cognito ID token
 * @returns {{ role: string|null, entityType: string|null, entityId: string|null, profileId: string|null }}
 */
export const getTokenContext = (idToken) => {
  if (!idToken) return { role: null, entityType: null, entityId: null, profileId: null };
  const payload = decodeJWT(idToken);
  if (!payload) return { role: null, entityType: null, entityId: null, profileId: null };
  return {
    role: payload["custom:role"] ?? payload.role ?? null,
    entityType: payload["custom:entityType"] ?? payload.entityType ?? null,
    entityId: payload["custom:entityId"] ?? payload.entityId ?? null,
    profileId: payload["custom:profileId"] ?? payload.profileId ?? payload.sub ?? null,
  };
};

/**
 * True when the active portal context is an Airport entity.
 * @param {string|null|undefined} entityType
 * @returns {boolean}
 */
export const isAirportEntityType = (entityType) =>
  String(entityType || "")
    .trim()
    .toLowerCase() === "airport";

/**
 * Get a valid token, refreshing if necessary
 * This function should be called before making API requests
 *
 * @param {string} currentToken - Current ID token
 * @returns {Promise<string>} - Valid ID token (refreshed if necessary)
 */
export const getValidToken = async (currentToken) => {
  // If no token provided, try to get current user
  if (!currentToken) {
    try {
      const user = await cognitoAuth.getCurrentUser();
      currentToken = user?.id_token;
      if (!currentToken) {
        throw new Error("No authentication token available");
      }
    } catch (error) {
      throw new Error("Authentication required. Please sign in again.");
    }
  }

  // Check if token is expired or will expire soon
  if (isTokenExpired(currentToken)) {
    console.log("Token expired or expiring soon, refreshing...");
    try {
      // Refresh the token
      const refreshedUser = await cognitoAuth.refreshToken();
      if (!refreshedUser || !refreshedUser.id_token) {
        throw new Error("Failed to refresh token");
      }
      return refreshedUser.id_token;
    } catch (error) {
      console.error("Token refresh failed:", error);
      // Try to get current session as fallback
      try {
        const user = await cognitoAuth.getCurrentUser();
        if (user && user.id_token && !isTokenExpired(user.id_token)) {
          return user.id_token;
        }
      } catch (e) {
        // If both refresh and getCurrentUser fail, throw error
        throw new Error(
          "Token expired and refresh failed. Please sign in again."
        );
      }
      throw new Error(
        "Token expired and refresh failed. Please sign in again."
      );
    }
  }

  // Token is still valid, return it
  return currentToken;
};
